/**
 * Vue Router for AI-Manual.
 *
 * - /login (blank layout, no auth)
 * - /manual, /manual/:manualId, /manual/:manualId/{video,document} (auth-gated)
 * - /account (auth-gated)
 * - /public/manual/:publicToken, .../{video,document} (anonymous, public layout
 *   hides AppBar / NavigationDrawer via themeStore.setPublicView)
 *
 * ルート名は poranos の Manual フロント（丸コピー）が push する名前・param に合わせる
 * (ManualDetail/ManualVideo/ManualDocument + :manualId, Public* + :publicToken,
 * props: true)。コピー済ビューを無改造で動かすための配線 delta (Spec #2)。
 */
import { createRouter, createWebHistory } from "vue-router";

import { useAuthStore } from "@/stores/authStore";
import { useThemeStore } from "@/stores/themeStore";

const routes = [
  {
    path: "/",
    redirect: "/manual",
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/LoginPage.vue"),
    meta: { requiresAuth: false, layout: "blank" },
  },
  {
    path: "/auth/okta/callback",
    name: "okta-callback",
    component: () => import("@/views/OktaCallback.vue"),
    meta: { requiresAuth: false, layout: "blank" },
  },
  {
    path: "/manual",
    name: "ManualList",
    component: () => import("@/views/Manual/ManualList.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/manual/:manualId",
    name: "ManualDetail",
    component: () => import("@/views/Manual/ManualDetail.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/manual/:manualId/video",
    name: "ManualVideo",
    component: () => import("@/views/Manual/ManualVideo.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/manual/:manualId/document",
    name: "ManualDocument",
    component: () => import("@/views/Manual/ManualDocument.vue"),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: "/account",
    name: "account-detail",
    component: () => import("@/views/AccountDetail.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/users",
    name: "admin-users",
    component: () => import("@/views/Admin/UserAdmin.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/superadmin/cost",
    name: "superadmin-cost",
    component: () => import("@/views/SuperAdmin/CostDashboard.vue"),
    meta: { requiresAuth: true, requiresSuperadmin: true },
  },
  {
    path: "/superadmin/settings",
    name: "superadmin-settings",
    component: () => import("@/views/SuperAdmin/AuthPolicy.vue"),
    meta: { requiresAuth: true, requiresSuperadmin: true },
  },
  {
    path: "/superadmin/staff",
    name: "superadmin-staff",
    component: () => import("@/views/SuperAdmin/StaffAdmin.vue"),
    meta: { requiresAuth: true, requiresSuperadmin: true },
  },

  // Public Manual routes (anonymous access via public_token).
  // meta.layout='public' tells the router beforeEach to call
  // themeStore.setPublicView(true) so AppBar / NavigationDrawer hide.
  {
    path: "/public/manual/:publicToken",
    name: "PublicManualDetail",
    component: () => import("@/views/Manual/PublicManualDetail.vue"),
    props: true,
    meta: { requiresAuth: false, layout: "public" },
  },
  {
    path: "/public/manual/:publicToken/video",
    name: "PublicManualVideo",
    component: () => import("@/views/Manual/PublicManualVideo.vue"),
    props: true,
    meta: { requiresAuth: false, layout: "public" },
  },
  {
    path: "/public/manual/:publicToken/document",
    name: "PublicManualDocument",
    component: () => import("@/views/Manual/PublicManualDocument.vue"),
    props: true,
    meta: { requiresAuth: false, layout: "public" },
  },

  {
    path: "/:pathMatch(.*)*",
    redirect: "/manual",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const themeStore = useThemeStore();

  themeStore.setPublicView(to.meta?.layout === "public");
  themeStore.setHideMenuBar(to.meta?.layout === "blank");

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ path: "/login", query: { redirect: to.fullPath } });
    return;
  }
  // admin ロール画面。backend も IsOrgAdmin で閉じているので、ここは UX のための gating。
  if (to.meta.requiresAdmin && authStore.userProfile.role !== "admin") {
    next({ path: "/manual" });
    return;
  }
  // superadmin (運営者) 画面。backend も IsSuperAdmin で閉じているので、ここは UX のための gating。
  if (to.meta.requiresSuperadmin && !authStore.userProfile.is_superadmin) {
    next({ path: "/manual" });
    return;
  }
  next();
});

export default router;
